import CancelIcon from '@material-ui/icons/Cancel';
import React from 'react';
import { useTransition } from 'react-spring';
import Logo from './Logo';
import HelpIcon from './HelpIcon';
import CheckIcon from './CheckIcon';
import PlusIcon from './PlusIcon';
import {
  ActivationCodeContainer,
  AnimationLayer,
  CallToAction,
  Code,
  CodeConnect,
  Container,
  CTACardContainer,
  CTAOr,
  DeviceStatus,
  Error,
  Footer,
  Header,
  Help,
  Inner,
  Label,
  Link,
  Main,
  Paragraph,
  RegisterDeviceHeader,
  StepBadge,
  StepDesciption,
  StepTitle,
} from './styles/SetupStep.styles';
import AddIcon from './AddIcon';
import scale from '../utilities/scale';
import MailIcon from './MailIcon';
import CTACard from './CTACard';
import QRCodeIcon from './QRCodeIcon';
import DisplaiLogoWhite from './DisplaiLogoWhite';
import DashQRCode from 'jsx:../../qrcodes/dash.svg';

export type BootStep =
  | 'register_device'
  | 'waiting_for_publish'
  | 'multiple_screens'
  | 'disabled_screen'
  | 'empty_playlist';

export interface SetupStepProps {
  deviceId: string;
  bootStep: BootStep;
  confirmationToken: string | null;
  signUpPage?: string;
  signUpQrCode?: React.ReactNode;
}

export default function SetupStep({
  deviceId,
  bootStep,
  confirmationToken,
  signUpPage,
  signUpQrCode,
}: SetupStepProps): JSX.Element {
  const transitions = useTransition(bootStep, null, {
    from: { opacity: 0, transform: 'translate3d(100%,0,0)' },
    enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
    leave: { opacity: 0, transform: 'translate3d(-50%,0,0)' },
  });

  const screens: Record<
    BootStep,
    {
      status?: string;
      helpPage?: string;
      inner: React.ReactNode;
      error?: React.ReactNode;
      backgroundColor?: string;
      footerBackgroundColor?: string;
    }
  > = {
    register_device: {
      inner: !!signUpPage ? (
        <CTACardContainer>
          <CTACard title="I have a Displai account">
            <Paragraph>
              Great! From the Displai dashboard screens page, select “Add”,
              click screen and then enter the code below.
            </Paragraph>
            <ActivationCodeContainer>
              <Label>ACTIVATION CODE</Label>
              <CodeConnect>{confirmationToken}</CodeConnect>
            </ActivationCodeContainer>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: scale(9.6),
              }}
            >
              <Paragraph>Or, scan the QR code below:</Paragraph>
              <QRCodeIcon
                qrCodeIcon={<DashQRCode />}
                labelLine1="Scan to go to"
                labelLine2="dash.displai.ai"
              />
            </div>
          </CTACard>
          <div style={{ alignSelf: 'center' }}>
            <CTAOr>OR</CTAOr>
          </div>
          <CTACard title="I don't have an account yet">
            <Paragraph>
              You’ll need access to the Displai web app to manage your screen
              and screen content.
            </Paragraph>
            <Paragraph>
              To get started, create a free account now at{' '}
              <Link href={`https://${signUpPage}`}>{signUpPage}</Link>
            </Paragraph>
            <Paragraph>Or, scan the QR code below:</Paragraph>

            <div style={{ flex: 1 }} />
            <QRCodeIcon
              qrCodeIcon={signUpQrCode}
              labelLine1="Scan to go to"
              labelLine2={
                <div style={{ fontSize: scale(24.6), lineHeight: scale(36.9) }}>
                  {signUpPage}
                </div>
              }
            />
          </CTACard>
        </CTACardContainer>
      ) : (
        <>
          <CallToAction>Pair your screen</CallToAction>
          <StepTitle>
            <StepBadge>
              <AddIcon />
            </StepBadge>
            Add screen to your dashboard with code{' '}
            <Code>{confirmationToken}</Code>
          </StepTitle>
          <StepDesciption>
            Use your computer to go to dash.displai.ai, click “Add Screen” and
            enter the above code.
          </StepDesciption>
        </>
      ),
    },

    waiting_for_publish: {
      status: 'Connected to internet and dashboard',
      backgroundColor: '#E7F7ED',
      footerBackgroundColor: '#D0EFDB',
      inner: (
        <>
          <CallToAction>Hooray! You’re all set</CallToAction>
          <StepTitle>
            <StepBadge>
              <CheckIcon />
            </StepBadge>
            This screen is ready to be amazing!
          </StepTitle>
          <StepDesciption>
            Add images, videos, and more from your Displai dashboard.
          </StepDesciption>
        </>
      ),
    },

    disabled_screen: {
      status: 'Connected to internet',
      backgroundColor: '#F3F3F3',
      footerBackgroundColor: '#DCDCDC',
      inner: (
        <>
          <CallToAction>This screen is inactive</CallToAction>
          <StepTitle
            style={{
              fontSize: scale(35),
              fontWeight: 500,
              lineHeight: scale(52.5),
            }}
          >
            <StepBadge>
              <MailIcon />
            </StepBadge>
            Please contact billing@displai.ai to reactivate.
          </StepTitle>
        </>
      ),
    },

    multiple_screens: {
      status: 'Connected to internet',
      backgroundColor: '#F3F3F3',
      footerBackgroundColor: '#DCDCDC',
      inner: (
        <>
          <CallToAction>This screen is already running</CallToAction>
          <StepTitle
            style={{
              fontSize: scale(35),
              fontWeight: 500,
              lineHeight: scale(52.5),
            }}
          >
            <StepBadge>
              <CancelIcon style={{ width: scale(81), height: scale(81) }} />
            </StepBadge>
            Close all other windows to start playing content.
          </StepTitle>
        </>
      ),
    },

    empty_playlist: {
      status: 'Connected to internet and dashboard',
      backgroundColor: '#F3F3F3',
      footerBackgroundColor: '#DCDCDC',
      inner: (
        <>
          <CallToAction>No content to display.</CallToAction>
          <StepTitle
            style={{
              fontSize: scale(35),
              fontWeight: 500,
              lineHeight: scale(52.5),
            }}
          >
            <StepBadge>
              <PlusIcon />
            </StepBadge>
            Try adding supported apps like Pictures and Flyers.
          </StepTitle>
        </>
      ),
    },
  };

  const isRegisterDeviceStep = bootStep === 'register_device';
  const status = screens[bootStep]?.status;
  const helpPage = screens[bootStep]?.helpPage;
  const error = screens[bootStep]?.error;
  const backgroundColor = screens[bootStep]?.backgroundColor;
  const footerBackgroundColor = screens[bootStep]?.footerBackgroundColor;

  return (
    <Container
      style={{ backgroundColor: backgroundColor }}
      signUpPage={signUpPage}
      registerDeviceStep={isRegisterDeviceStep}
    >
      {isRegisterDeviceStep && signUpPage ? (
        <RegisterDeviceHeader>
          <DisplaiLogoWhite />
        </RegisterDeviceHeader>
      ) : (
        <Header>
          <Logo />
        </Header>
      )}

      <Main>
        {transitions.map(({ item, props, key }) => {
          return (
            <AnimationLayer key={key} style={props}>
              <Inner>{screens[item]?.inner}</Inner>
            </AnimationLayer>
          );
        })}
      </Main>
      {error && <Error>{error}</Error>}
      {!(isRegisterDeviceStep && signUpPage) && (
        <Footer
          hasError={!!error}
          style={{
            backgroundColor: !!error ? '#edde4f' : footerBackgroundColor,
          }}
        >
          <DeviceStatus>
            {status && (
              <div
                style={{
                  fontSize: scale(30),
                  fontWeight: 400,
                  lineHeight: scale(33.45),
                }}
              >
                {' '}
                <strong>Status</strong>: {status}
              </div>
            )}
            <div
              style={{
                width: scale(960),
                height: scale(38),
                fontWeight: 400,
                fontSize: scale(20),
                lineHeight: scale(31.6),
                paddingTop: scale(15),
              }}
            >
              ID: {deviceId}
            </div>
          </DeviceStatus>
          <Help>
            <HelpIcon />
            <span>
              Need help? Go to displai.ai/start{' '}
              <Link href={`https://www.${helpPage}`}>{helpPage}</Link>
            </span>
          </Help>
        </Footer>
      )}
    </Container>
  );
}
